// CrystalClear Vaults — Browse Vaults Page

import { VAULT_CONFIGS, VAULT_ABI, USDC_DECIMALS, SHARE_DECIMALS, EXPLORER, APP_MODE } from '../config.js';
import { getAddress, getReadProvider } from '../wallet.js';
import { isStealth, isDepositor, canBypassStealth, launchDateShort } from '../stealth-mode.js';
import { refreshAllAprCells, refreshAllWinRateCells, refreshAllMaxDdCells, refreshAllSharePriceCells } from '../performance.js';

export function renderVaults() {
  return `
  <header class="mb-12">
    <h1 class="font-headline text-5xl text-on-surface mb-4 tracking-tight">Vaults</h1>
    <p class="font-body text-on-surface-variant text-lg max-w-2xl">Market-neutral systematic trading on Hyperliquid. One strategy, live &amp; verifiable since April 2026 — these vaults are how you access it.</p>
  </header>

  <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20" id="vault-cards">
    <div class="text-center text-secondary/40 py-12 col-span-full">Loading vaults...</div>
  </div>
  `;
}

function _metricDisplay(value, suffix = "", decimals = 1) {
  if (value === null || value === undefined) return "--";
  return typeof value === "number" ? value.toFixed(decimals) + suffix : String(value);
}

export async function initVaults() {
  const rp = getReadProvider();
  const cards = [];
  const stealth = isStealth();
  const addr = getAddress();
  // Depositor whitelist: an already-deposited wallet sees full metrics + the
  // live "View Vault" CTA even in stealth. New deposits stay gated by the
  // vault-detail page (CTA there is a no-op in stealth for everyone).
  // Whitelisted testers count as "depositor" for UI purposes — they get
  // unhidden metrics + the live "View Vault" CTA (per-vault deposit CTA
  // unlocks inside vault-detail).
  const depositor = stealth && addr
    ? (canBypassStealth(addr) || await isDepositor(addr))
    : false;
  const hideMetrics = stealth && !depositor;

  // Visibility gate: hide any vault that's flagged `visibility: "internal"`
  // in config.js (defence-in-depth — registry is the source of truth, but
  // if a shadow vault ever creeps into VAULT_CONFIGS by mistake it must
  // not render on the public cards grid). Defaults to "public" via ??.
  const publicVaults = VAULT_CONFIGS.filter(v => (v.visibility ?? "public") === "public");

  // Sort: live vaults first (most prominent), then coming_soon + retired
  // (both render as "Coming Soon" placeholders — see
  // GEMSTONE_RETIREMENT_DESIGN.md). Within a bucket, preserve config order.
  const statusRank = (s) => (s === "live" ? 0 : 1);
  const sortedVaults = [...publicVaults].sort(
    (a, b) => statusRank(a.status) - statusRank(b.status)
  );

  // Parallelize the per-vault on-chain reads. Was sequential `for...of`
  // with Promise.all _inside_, so 6 live vaults × 4 round-trips × ~300ms =
  // ~3.6s. Promise.all over the outer loop collapses to ~max(900ms) ≈ 1s.
  // Coming-soon vaults need no reads (constant time).
  // Wedged-wallet / dead-RPC resilience (transparency spec P0b): the on-chain
  // reads below gate the WHOLE grid render. With a misbehaving wallet extension
  // or an unreachable RPC, an un-timed-out await hangs "Loading vaults…" forever
  // with no console error. Cap each vault's reads at 5s — on timeout we render
  // the card with placeholder TVL/share-price (APR fills from the edge API,
  // a separate path). The grid ALWAYS paints within 5s. See
  // feedback_router_rerender_clobbers_async_dom_refs (same bug family).
  const READ_TIMEOUT_MS = 5000;
  const _withTimeout = (p) => Promise.race([
    p,
    new Promise((_, rej) => setTimeout(() => rej(new Error("on-chain read timeout")), READ_TIMEOUT_MS)),
  ]);
  const cardData = await Promise.all(sortedVaults.map(async (vc) => {
    const isLive = vc.status === "live" && vc.address;
    const reveal = isLive && !hideMetrics;
    let tvl = 0;
    let sharePrice = 1.0;
    let perfFeeStr = "15";
    if (reveal) {
      try {
        const vault = new ethers.Contract(vc.address, VAULT_ABI, rp);
        // Fire all 3 reads + assetsPerShare in parallel — was assetsPerShare
        // sequential after Promise.all, two round-trips per vault not one.
        // We don't yet know if totalSupply>0, but convertToAssets(10^9)
        // returns 0 cheaply when supply is 0 so always firing it is safe.
        const oneShare = 10n ** BigInt(SHARE_DECIMALS);
        const [totalAssets, totalSupply, perfFee, assetsPerShare] = await _withTimeout(Promise.all([
          vault.totalAssets(),
          vault.totalSupply(),
          vault.performanceFeeBps(),
          vault.convertToAssets(oneShare),
        ]));
        tvl = Number(totalAssets) / 10 ** USDC_DECIMALS;
        if (totalSupply > 0n && totalAssets > 0n) {
          const rawPrice = Number(assetsPerShare) / 10 ** USDC_DECIMALS;
          sharePrice = rawPrice / (vc.launchSharePrice || 1.0);
        }
        perfFeeStr = (Number(perfFee) / 100).toFixed(0);
      } catch (e) {
        // Timeout or RPC error → render the card with placeholders rather than
        // hang the whole grid. tvl/sharePrice stay at safe defaults.
        console.warn(`vaults: on-chain read for ${vc.name} failed/timed out — rendering card anyway:`, e.message);
      }
    }
    return { vc, tvl, sharePrice, perfFeeStr };
  }));

  // Render in the same sort order Promise.all preserved.
  for (const { vc, tvl, sharePrice, perfFeeStr } of cardData) {
    const isLive = vc.status === "live" && vc.address;
    // Retired renders identically to coming_soon (grayed, "Coming Soon", no
    // live metrics, not clickable). The −40% / "retired" never shows publicly.
    const isComingSoon = vc.status === "coming_soon" || vc.status === "retired";
    const reveal = isLive && !hideMetrics;
    // Legacy founding-fleet vault closed to NEW deposits (withdraw stays open).
    const depositClosed = isLive && vc.deposit_closed === true;

    const m = reveal ? (vc.metrics || {}) : {};  // stealth: no metrics
    const priceDisplay = reveal ? `$${sharePrice.toFixed(2)}` : '—';
    const contractUrl = vc.address ? EXPLORER + "/address/" + vc.address : "#";

    // Capacity bar (replaces plain TVL) — uniform with the landing cards. Fill
    // floored to a visible sliver and capped below full; closed seed vaults read
    // "$X seed" (no "room to grow"), the open vault keeps it. Real TVL stays in
    // the caption. 50k = early-stage target, used only for the fill (not shown).
    const capLabel = depositClosed ? 'Seed' : 'Capacity';
    const capPct = reveal ? Math.min(92, Math.max(6, (tvl / 50000) * 100)).toFixed(1) : 0;
    const capCaption = !reveal ? '—'
      : (depositClosed ? `$${Math.round(tvl)} seed` : `$${Math.round(tvl)} · room to grow`);
    // Seed-vault badge — uniform with the landing (the deposit-closed founding vaults).
    const seedBadge = depositClosed
      ? `<span class="bg-surface-container text-on-surface-variant text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-sm border border-outline-variant/25">Seed vault</span>`
      : '';

    // Target APR is always colored — it's a conservative backtest claim we DO
    // advertise in stealth. Other ratios stay muted pre-launch because their
    // backtest/live divergence is known to be material.
    const aprColor = isLive ? "text-primary" : "text-on-surface-variant";
    const winColor = reveal ? "text-on-surface" : "text-on-surface-variant";
    // Max DD shown in neutral text (was text-error / red — too alarming for
    // a normal backtest stat that's well within design limits).
    const ddColor = reveal ? "text-on-surface" : "text-on-surface-variant";
    const priceColor = reveal ? "text-on-surface" : "text-on-surface-variant";

    // Target APR: shown for any live vault (stealth or public), dashed only
    // for coming_soon where we haven't committed to a number.
    const aprDisplay = (isLive && vc.metrics?.targetApr != null)
      ? `~${vc.metrics.targetApr}%`
      : (vc.metrics?.targetApr != null ? `~${vc.metrics.targetApr}%` : '—');
    // medianHoldHours: live empirical median, computed from closed-trades
    // parquet. NOT backtest. Replaces the old Trades/yr stat — hold time
    // is a more user-relevant fact ("how long are positions open") and
    // ties into the macro-independence story.
    const holdDisplay = (isLive && vc.metrics?.medianHoldHours != null)
      ? `~${vc.metrics.medianHoldHours.toFixed(1)}h`
      : '—';

    // APR cell: live vaults render a loading shimmer that refreshAllAprCells()
    // swaps for the real Live APR — so we never flash the backtest "Target APR"
    // number and then change it. Coming-soon / retired vaults keep the static
    // Target APR (no live data will ever arrive for them).
    const aprLoading = isLive;
    const aprCellLabel = aprLoading ? "APR" : "Target APR";
    const aprCellValue = aprLoading
      ? '<span class="inline-block w-14 h-5 rounded bg-on-surface/10 animate-pulse align-middle"></span>'
      : aprDisplay;
    const aprCellSub = aprLoading ? "&nbsp;" : "backtest est.";
    const aprCellTitle = aprLoading
      ? "Live APR — annualized return since launch, computed from on-chain share price."
      : "Target APR — aspirational 12-month return based on backtested performance. Not a guarantee; live results will vary with market regime.";

    const cardBg = isLive
      ? "bg-surface-variant/40 backdrop-blur-[24px] hover:border-primary/30"
      : "bg-surface-container-low";
    const cardOpacity = isComingSoon ? "opacity-70" : "";

    cards.push(`
      <div class="${cardBg} rounded-lg p-6 flex flex-col justify-between border border-outline-variant/15 relative overflow-hidden group transition-colors duration-500 ${cardOpacity}"
           data-vault-key="${vc.key}"
           ${isLive ? `onclick="window.location.hash='#vault/${vc.key}'" style="cursor:pointer"` : ''}>
        ${isLive ? '<div class="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>' : ''}
        <div class="relative z-10">

          <!-- Header -->
          <div class="flex justify-between items-start mb-6">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-lg ${vc.color.bg20} flex items-center justify-center">
                <span class="material-symbols-outlined ${vc.color.text} text-lg">${vc.icon}</span>
              </div>
              <div>
                <div class="flex items-center gap-2 mb-0.5 flex-wrap">
                  <h2 class="font-headline text-2xl text-on-surface tracking-wide">${vc.name}</h2>
                  ${isLive && !hideMetrics
                    ? `<span class="bg-surface-container text-primary text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-sm border border-primary/20 flex items-center gap-1">
                        <span class="w-1.5 h-1.5 rounded-full bg-primary animate-ping"></span> Live
                       </span>`
                    : isLive && hideMetrics
                      ? `<span class="bg-surface-container text-primary/80 text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-sm border border-primary/20 flex items-center gap-1">
                          <span class="w-1.5 h-1.5 rounded-full bg-primary animate-ping"></span> Trading · Opens ${launchDateShort()}
                         </span>`
                      : `<span class="bg-surface-container text-on-surface-variant text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-sm border border-outline-variant/30">Coming Soon</span>`
                  }
                  ${seedBadge}
                  ${depositClosed ? `<span class="bg-surface-container text-on-surface-variant text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-sm border border-outline-variant/30" title="This founding-fleet vault is closed to new deposits. Existing depositors can withdraw anytime; the track record stays live and verifiable.">Closed to deposits · withdraw open</span>` : ''}
                </div>
                <p class="font-body text-sm text-secondary">${vc.subtitle}</p>
              </div>
            </div>
          </div>

          <!-- Metrics Grid -->
          <div class="grid grid-cols-3 gap-6 mb-6">
            <div data-apr-cell="${vc.key}" title="${aprCellTitle}">
              <p data-apr-label class="font-label text-xs uppercase text-on-surface-variant tracking-wider mb-1">${aprCellLabel}</p>
              <p data-apr-value class="font-headline text-xl tabular-nums ${aprColor}">${aprCellValue}</p>
              <p data-apr-subtitle class="text-[0.55rem] uppercase tracking-wider text-on-surface-variant/60 mt-0.5">${aprCellSub}</p>
            </div>
            <div data-winrate-cell="${vc.key}" title="Win Rate — share of closed positions that finished profitable. Live (from on-chain trades) once the vault has ≥10 closed positions; otherwise the design-intent target. Strategies use high reward-to-risk ratios, so a sub-50% win rate is by design: winners are larger than losers.">
              <p class="font-label text-xs uppercase text-on-surface-variant tracking-wider mb-1">Win Rate</p>
              <p class="font-headline text-xl tabular-nums ${winColor}" data-winrate-value data-vault-addr="${vc.address || ''}">${_metricDisplay(m.winRate, "%", 0)}</p>
            </div>
            <div title="Max Drawdown (live since launch) — worst peak-to-trough share-price decline observed since this vault went live. Backtest MDDs are lower; live experience reflects the live execution environment.">
              <p class="font-label text-xs uppercase text-on-surface-variant tracking-wider mb-1">Max DD</p>
              <p class="font-headline text-xl tabular-nums ${ddColor}" data-metric="max-dd">${reveal ? '<span class="inline-block w-12 h-5 rounded bg-on-surface/10 animate-pulse align-middle"></span>' : _metricDisplay(m.maxDD, "%", 1)}</p>
            </div>
            <div title="Share Price — current $ value of one vault share. Starts at $1.00 at launch; rises as the vault profits.">
              <p class="font-label text-xs uppercase text-on-surface-variant tracking-wider mb-1">Share Price</p>
              <p class="font-headline text-xl tabular-nums ${priceColor}" data-metric="share-price">${priceDisplay}</p>
            </div>
            <div title="Proprietary seed capital — ${depositClosed ? 'this vault is closed to new deposits.' : 'early stage, room to grow.'}">
              <p class="font-label text-xs uppercase text-on-surface-variant tracking-wider mb-1">${capLabel}</p>
              <div class="mt-2.5">
                <div class="h-1.5 rounded-full bg-on-surface/10 overflow-hidden">
                  <div class="h-full rounded-full" style="width:${capPct}%;background:linear-gradient(90deg,#6af2de,#2fd9f4)"></div>
                </div>
                <span class="text-[0.55rem] uppercase tracking-wider text-on-surface-variant/60 block mt-1.5">${capCaption}</span>
              </div>
            </div>
            <div title="Avg holding time (live since launch) — median duration a position stays open, measured across all closed trades. Short hold time = macro factors don't have time to dominate the outcome of any single trade.">
              <p class="font-label text-xs uppercase text-on-surface-variant tracking-wider mb-1">Avg Hold</p>
              <p class="font-headline text-xl tabular-nums ${winColor}">${holdDisplay}</p>
            </div>
          </div>

          <!-- Tags — each is a quick-look characteristic. Tooltips clarify
               anything that could be ambiguous (notably: candle timeframes
               are NOT hold time — hold time varies, median across the fleet
               is ~9h; see landing page "Uncorrelated by design"). -->
          <div class="flex flex-wrap gap-2 mb-8">
            <span class="bg-surface-container-low text-secondary-fixed text-xs px-2.5 py-1 rounded-DEFAULT border border-outline-variant/15 font-label tracking-wide" title="Performance fee on profits only. No management fee, no subscription.">${perfFeeStr}% perf fee</span>
            <span class="bg-surface-container-low text-secondary-fixed text-xs px-2.5 py-1 rounded-DEFAULT border border-outline-variant/15 font-label tracking-wide" title="Number of crypto assets this vault trades. All are traded long AND short.">${m.coins || '?'} coins · long/short</span>
            <span class="bg-surface-container-low text-secondary-fixed text-xs px-2.5 py-1 rounded-DEFAULT border border-outline-variant/15 font-label tracking-wide" title="Candle timeframes the strategies trade on — NOT hold time. Hold time varies per trade; median across the fleet is ~9 hours.">${m.timeframes || '?'} candles</span>
          </div>
        </div>

        <!-- CTA Button -->
        ${isLive && !hideMetrics ? `
          <button class="relative z-10 w-full bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold py-3 px-4 rounded-DEFAULT hover:shadow-[0_0_24px_rgba(138,235,255,0.4)] transition-all duration-300 active:scale-[0.98]">
            View Vault
          </button>
        ` : isLive && hideMetrics ? `
          <button class="relative z-10 w-full bg-surface-container-high text-secondary font-headline py-3 px-4 rounded-DEFAULT hover:bg-surface-variant/50 border border-outline-variant/30 transition-colors">
            Preview — deposits open ${launchDateShort()}
          </button>
        ` : `
          <button class="relative z-10 w-full bg-surface-container-high text-on-surface-variant font-headline py-3 px-4 rounded-DEFAULT cursor-not-allowed border border-outline-variant/15">
            Launching soon
          </button>
        `}
      </div>
    `);
  }

  document.getElementById("vault-cards").innerHTML = cards.join("");
  // Upgrade target-APR cells to live-APR for vaults with ≥ 14 d of data.
  // No-op in stealth (helper returns target results without fetching).
  refreshAllAprCells();
  // Upgrade the Max DD tile from the static backtest value to the LIVE drawdown
  // (server-computed scalar). Matches the landing + the tile's own "live" tooltip.
  refreshAllMaxDdCells();
  // Upgrade backtest Win Rate to live (from on-chain trades) for vaults with
  // ≥ 10 closed positions. Hits the corrected /api/account/{addr}/fills.
  refreshAllWinRateCells();
  // Upgrade Share Price from the client-side on-chain read to the reliable
  // Lambda value, so a browser that gets rate-limited on the public HL RPC
  // (~4 calls/card) no longer silently shows the $1.00 default.
  refreshAllSharePriceCells();
}
